/**
 * Gamification colors — independent of user theme.
 * These stay constant across all themes to maintain
 * consistent game identity.
 */

export const GAMIFICATION_COLORS = {
  // XP system — Gold per CLAUDE.md
  xpGold: '#D4A017',
  xpGoldLight: '#FFD700',
  xpGoldDark: '#B8860B',
  xpGoldGlow: 'rgba(212, 160, 23, 0.3)',

  // Gems system — Turquoise per CLAUDE.md
  gemsTurquoise: '#00CED1',
  gemsTurquoiseLight: '#7FFFD4',
  gemsTurquoiseDark: '#008B8B',
  gemsTurquoiseGlow: 'rgba(0, 206, 209, 0.3)',

  // Streak system — Amber/orange + Light blue per CLAUDE.md
  streakFire: '#FF8C00',
  streakFireGlow: 'rgba(255, 140, 0, 0.3)',
  streakIce: '#87CEEB',
  streakIceGlow: 'rgba(135, 206, 235, 0.3)',

  // Level-up
  levelUp: '#A855F7',
  levelUpGlow: 'rgba(168, 85, 247, 0.4)',

  // Badge rarity per CLAUDE.md
  badgeCommon: '#CD7F32',       // Bronze
  badgeRare: '#C0C0C0',         // Silver
  badgeLegendary: '#FFD700',    // Gold
  badgeHidden: '#2E8B57',       // Dark green

  // Confetti palette
  confettiColors: ['#D4A017', '#00CED1', '#A855F7', '#FF8C00', '#CD7F32', '#2E8B57'],
} as const;

/**
 * XP level thresholds — each index = level, value = cumulative XP needed.
 */
export const LEVEL_THRESHOLDS = [
  0, 100, 300, 600, 1000, 1500, 2100, 2800, 3600, 4500,
  5500, 6600, 7800, 9100, 10500, 12000, 13600, 15300, 17100, 19000, 21000,
] as const;

/**
 * Level names from the Blueprint.
 */
export const LEVEL_NAMES = [
  'Newborn Unicorn', 'Wobbly Foal', 'Curious Colt', 'Trotting Trainee',
  'Galloping Go-Getter', 'Spiraling Horn', 'Rainbow Runner', 'Celestial Cantor',
  'Mythic Maverick', 'Star Strider', 'Nova Nomad', 'Comet Chaser',
  'Astral Artisan', 'Cosmic Crafter', 'Dimension Dancer', 'Ethereal Engineer',
  'Quantum Quester', 'Infinity Igniter', 'Paradigm Pioneer', 'Omega Oracle',
  'Legendary Johnicorn',
] as const;

/**
 * Get level progress percentage for a given XP.
 */
export function getLevelProgress(xp: number) {
  let level = 0;
  for (let i = LEVEL_THRESHOLDS.length - 1; i >= 0; i--) {
    if (xp >= (LEVEL_THRESHOLDS[i] ?? 0)) {
      level = i;
      break;
    }
  }

  const currentThreshold = LEVEL_THRESHOLDS[level] ?? 0;
  const nextThreshold = (LEVEL_THRESHOLDS[level + 1] ?? currentThreshold + 2000);
  const xpInLevel = xp - currentThreshold;
  const xpForNext = nextThreshold - currentThreshold;
  const percentage = Math.min((xpInLevel / xpForNext) * 100, 100);

  return {
    level,
    levelName: LEVEL_NAMES[level] ?? 'Legendary Johnicorn',
    percentage,
    xpInLevel,
    xpForNext,
  };
}
